import {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react'
import useInfiniteLoad from '@/hooks/infiniteLoad'
import styles from '@/styles/search.module.scss'
import Head from 'next/head'
import BoxProduct, { ProductSkeleton } from '@/components/box-product/BoxProduct'
import { search, searchCount, getHighestPrice } from '@/service/product-list.api'
import Select from '@/components/general/Select/Select'
import MultiSelect from '@/components/general/Select/MultiSelect'
import SelectRange from '@/components/general/Select/SelectRange'
import Input from '@/components/general/Input/Input'
import Button from '@/components/general/Button/Button'
import { useRouter } from 'next/router'
import { useSnackBar } from '@/context/app-context'
import { addCommas } from '@/utils/formatter'
import { getBrands } from '@/service/brands.api'
import { getCategories } from '@/service/categories.api'
import NoData from '@/components/general/NoData/NoData'

export default function SearchWatch() {
  const router = useRouter()
  const snackbar = useSnackBar()
  const limit = 16
  const initialCurrentPage = 1
  const [currentPage, setCurrentPage] = useState(initialCurrentPage)
  const productListRef = useRef()
  const [loading, setLoading] = useState(true)
  const [firstLoad, setFirstLoad] = useState(true)
  const [productList, setProductList] = useState([])
  const [countAll, setCountAll] = useState(0)
  const [filterList, setFilterList] = useState({})
  const [filterData, setFilterData] = useState()
  const [sortBy, setSortBy] = useState('isPostByAdmin:DESC,updatedAt:DESC')
  const [filterChanged, setFilterChanged] = useState(false)
  const [searchFilter, setSearchFilter] = useState(router.query.keyword || '')
  const minPrice = 0
  const [maxPrice, setMaxPrice] = useState(10000)
  const [priceFilter, setPriceFilter] = useState()

  const currentOffset = useMemo(() => (currentPage - 1) * limit, [currentPage])

  const maxPage = useMemo(() => Math.ceil(countAll / limit), [countAll])

  const brandFilter = useMemo(() => filterList.brands?.map((item) => ({
    text: item.brandName,
    value: item.id
  })), [filterList])

  const categoriesFilter = useMemo(() => filterList.categories?.map((item) => ({
    text: item.name,
    value: item.id
  })), [filterList])

  const fetchFilterList = useCallback(async () => {
    const [brandResponse, categoriesResponse] = await Promise.all([getBrands(), getCategories()])

    setFilterList({
      brands: brandResponse.data.brands,
      categories: categoriesResponse.data.categories
    })
  }, [])

  const resetPriceFilter = () => setPriceFilter()

  const fetchMaxPrice = useCallback(async (filter) => {
    const priceResponse = await getHighestPrice(filter)
    setFilterChanged(false)
    setMaxPrice(priceResponse.data.userProducts?.[0]?.price || 10000)
    resetPriceFilter()
  }, [])

  const resetPage = useCallback(() => {
    setProductList([])
    setCurrentPage(1)
  }, [])

  const handlePriceFilterChange = (price) => {
    resetPage()
    setPriceFilter(price)
    let priceQuery = {}
    if (price.length) {
      priceQuery = {
        price_gte: price[0],
        price_lte: price[1]
      }
    }
    setFilterData((prev) => ({
      ...prev,
      ...priceQuery
    }))
  }

  const handleClearFilter = () => {
    if (Object.keys(filterData).length) {
      resetPage()
      setUrlParam()
      setFilterChanged(true)
      setSearchFilter('')
      resetPriceFilter()
      setFilterData({})
      fetchMaxPrice({})
    }
  }

  const setUrlParam = (keyword) => {
    router.push(`${window.location.pathname}/${keyword ? '?keyword=' + keyword : ''}`, null, { shallow: true })
  }

  useEffect(() => {
    if (sortBy) {
      resetPage()
    }
  }, [sortBy, resetPage])

  const fetchProduct = useCallback(async (filter, offset) => {
    setLoading(true)
    try {
      const response = await search(filter, offset, limit, sortBy || undefined)
      setProductList((prev) => ([...prev, ...response.data.userProducts]))
    } catch (e) {
      snackbar({
        isOpen: true,
        type: 'error',
        content: 'Cannot fetch product, please try again.'
      })
    } finally {
      setLoading(false)
    }
  }, [snackbar, sortBy])

  const fetchCountAll = useCallback(async (filter) => {
    const response = await searchCount(filter)
    setCountAll(response.data.userProductsCount)
    return () => setCountAll(0)
  }, [])

  const doSearch = useCallback((keyword) => {
    resetPage()
    // setSearchFilter(keyword)
    setUrlParam(keyword)
    const query = {
      _or: [
        {
          name_contains: keyword
        },
        {
          model_contains: keyword
        },
        {
          description_contains: keyword
        }
      ]
    }
    setFilterData((prev) => {
      const prevFilter = { ...prev }
      delete prevFilter.price_gte
      delete prevFilter.price_lte
      return { ...prevFilter, ...query }
    })
    setFilterChanged(true)
  }, [])

  const handleFilterChange = (field, value) => {
    setFilterChanged(true)
    resetPage()
    setFilterData((prev) => {
      const prevFilter = { ...prev }
      delete prevFilter.price_gte
      delete prevFilter.price_lte
      return {
        ...prevFilter,
        [field]: value
      }
    })
  }

  const handleSearchFilterKeyDown = useCallback((e) => {
    if (e.keyCode === 13 && !loading) {
      doSearch(searchFilter)
    }
  }, [doSearch, searchFilter, loading])

  const transformFilterToGraphqlQuery = (filter) => {
    const whereFilter = {}
    if (filter._or) {
      whereFilter._or = [...filter._or]
    }
    Object.keys(filter).forEach((key) => {
      if (key !== '_or') {
        if (Array.isArray(filter[key])) {
          if (filter[key].length) {
            whereFilter[`${key}_in`] = filter[key]
          }
        } else {
          whereFilter[key] = filter[key]
        }
      }
    })
    return whereFilter
  }

  useEffect(() => {
    fetchFilterList()
  }, [fetchFilterList])

  useEffect(() => {
    if (router.isReady && firstLoad) {
      resetPage()
      if (Object.keys(router.query).length) {
        const filterObj = {}
        Object.keys(router.query).forEach((key) => {
          if (key === 'keyword') {
            setSearchFilter(router.query[key])
            const query = [
              {
                name_contains: router.query[key]
              },
              {
                model_contains: router.query[key]
              },
              {
                description_contains: router.query[key]
              }
            ]
            filterObj._or = [ ...query ]
          } else {
            filterObj[key] = router.query[key].indexOf(',') > 0 ? [router.query[key].split(',')] : router.query[key]
          }
        })
        setFilterData(filterObj)
        setFilterChanged(true)
        // doSearch(keyword)
      } else {
        setFilterData({})
        setFilterChanged(true)
      }
      setFirstLoad(false)
    }
  }, [router.query, router.isReady, firstLoad])

  useEffect(() => {
    if (filterChanged) {
      const query = transformFilterToGraphqlQuery(filterData || {})
      fetchCountAll(query)
    }
  }, [filterChanged, filterData, fetchCountAll])

  useEffect(() => {
    if (filterChanged) {
      const query = transformFilterToGraphqlQuery(filterData || {})
      fetchMaxPrice(query)
    }
  }, [filterChanged, fetchMaxPrice, filterData])

  useEffect(() => {
    if (filterChanged) {
     resetPage()
    }
  }, [filterChanged, resetPage])

  useEffect(() => {
    if (filterData) {
      const query = transformFilterToGraphqlQuery(filterData || {})
      fetchProduct(query, currentOffset)
    }
  }, [fetchProduct, filterData, currentOffset])

  const loadMore = () => {
    if (currentPage < maxPage && !loading) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  useInfiniteLoad(productListRef, loadMore)

  return (
    <>
      <Head>
        <title>Expert - Watch Repair Center | Search</title>
      </Head>
      <div className={styles.searchPage}>
        <div className="max-width">
          <div className={styles.filterTitle}>
            Search Marketplace
            {' '}
            {
              countAll > 0 && <span className={styles.count}>{`${countAll} result found`}</span>
            }
            <div className={`${styles.clearFilter} ${filterData && !!Object.keys(filterData).length && styles.show}`} onClick={handleClearFilter}>Clear Filters</div>
          </div>
          <div className={styles.filterWrap}>
            <div className={styles.inputWithButton}>
              <Input
                value={searchFilter}
                stylingClass={styles.input}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Keyword"
                onKeyDown={handleSearchFilterKeyDown}
              />
              <Button
                onClick={() => doSearch(searchFilter)}
                stylingClass={styles.button}
              >
                Search
              </Button>
            </div>
            {/* <MultiSelect
              stylingClass={styles.dropdown}
              placeholder="Brand"
              maxSelectItem={3}
              value={filterData?.brand || []}
              onSelect={(values) => handleFilterChange('brand', values.map((val) => val.value))}
              items={brandFilter}
            /> */}
            <Select
              stylingClass={styles.dropdown}
              placeholder="Brand"
              value={filterData?.brand || []}
              onSelect={(val) => handleFilterChange('brand', val.value)}
              items={brandFilter}
            />
            <MultiSelect
              stylingClass={styles.dropdown}
              placeholder="Categories"
              value={filterData?.categories || []}
              onSelect={(values) => handleFilterChange('categories', values.map((val) => val.value))}
              items={categoriesFilter}
            />
            <SelectRange
              stylingClass={styles.dropdown}
              placeholder="Price"
              listMinWidth={300}
              min={minPrice}
              max={maxPrice}
              range={100}
              onSelect={(val) => handlePriceFilterChange(val)}
              value={priceFilter}
            />
            {/* <Select
              stylingClass={styles.dropdown}
              placeholder="Model"
              value={filterData?.model || {}}
              onSelect={(val) => handleFilterChange('model', val.value)}
              items={brandFilter}
            /> */}
            <Select
              stylingClass={styles.dropdown}
              placeholder="Sort by"
              value={sortBy}
              onSelect={(val) => setSortBy(val.value)}
              items={[
                {
                  text: 'Relevance',
                  value: 'isPostByAdmin:DESC,updatedAt:DESC'
                },
                {
                  text: 'Newest',
                  value: 'updatedAt:DESC'
                },
                {
                  text: 'Model',
                  value: 'model:ASC'
                },
                {
                  text: 'Highest Price',
                  value: 'price:DESC'
                },
                {
                  text: 'Lowest Price',
                  value: 'price:ASC'
                }
              ]}
            />
          </div>
          <section className={styles.productList} ref={productListRef}>
            {
              !productList.length && !loading
                ? (
                  <NoData />
                )
                : (
                  <>
                    {
                      productList.map((item) => (
                        <BoxProduct
                          key={item.id}
                          url={`/product/${item.id}`}
                          image={item.images?.[0]?.url || ''}
                          title={item.name}
                          price={`฿ ${addCommas(item.price?.toString() || '0')}`}
                          updatedAt={item.updatedAt}
                          subTitle={item.model}
                          category={item.brand?.brandName || 'Others'}
                        />
                      ))
                    }
                    {
                      loading && <ProductSkeleton />
                    }
                  </>
                )
            }
          </section>
        </div>
      </div>
    </>
  )
}
